<template>
  <div class="machine-translate-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('machineTranslateManage.page.title')" :subtitle="$t('machineTranslateManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="machineTranslateManageInfo.conditions.typeCd" clearable class="w-full"
            :placeholder="$t('machineTranslateManage.search.typeCd')">
            <el-option :label="$t('machineTranslateManage.search.selectType')" value="" />
            <el-option v-for="(item, index) in machineTranslateManageInfo.typeCds" :key="index" :label="item.name"
              :value="item.statusCd" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="machineTranslateManageInfo.conditions.objId" clearable class="w-full"
            :placeholder="$t('machineTranslateManage.search.objId')" @keyup.enter.native="queryMachineTranslateMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="machineTranslateManageInfo.conditions.objName" clearable class="w-full"
            :placeholder="$t('machineTranslateManage.search.objName')" @keyup.enter.native="queryMachineTranslateMethod" />
        </el-col>
      </el-row>
      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryMachineTranslateMethod">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetMachineTranslateMethod">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event" @refresh="listMachineTranslates">
        <template #left>
          <span class="ds-panel__title">{{ $t('machineTranslateManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="machineTranslateManageInfo.machineTranslates" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('machineTranslateManage.page.empty')"
        @retry="listMachineTranslates">
        <el-table-column prop="objId" :label="$t('machineTranslateManage.table.objId')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.objId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="typeCdName" :label="$t('machineTranslateManage.table.typeCd')" align="center" width="120" />
        <el-table-column prop="objName" :label="$t('machineTranslateManage.table.objName')" align="center" min-width="140" />
        <el-table-column prop="machineCmdName" :label="$t('machineTranslateManage.table.machineCmd')" align="center" min-width="140" />
        <el-table-column prop="stateName" :label="$t('machineTranslateManage.table.state')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag size="sm" :type="scope.row.state === '20000' ? 'success' : 'warning'"
              :label="scope.row.stateName || '-'" :text="scope.row.stateName" />
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('machineTranslateManage.table.remark')" align="center" min-width="120" />
        <el-table-column prop="updateTime" :label="$t('machineTranslateManage.table.updateTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.updateTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="120" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state !== '20000'" size="mini" type="primary"
              @click="openEditMachineTranslateModel(scope.row)">
              {{ $t('machineTranslateManage.button.resync') }}
            </el-button>
            <span v-else class="ds-num">-</span>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 组件 -->
    <add-machine-translate ref="addMachineTranslate" @success="handleSuccess" />
    <edit-machine-translate ref="editMachineTranslate" @success="handleSuccess" />
  </div>
</template>

<script>
import { listMachineTranslates } from '@/api/machine/machineTranslateManageApi'
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import AddMachineTranslate from '@/components/machine/addMachineTranslate'
import EditMachineTranslate from '@/components/machine/editMachineTranslate'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'MachineTranslateManageList',
  components: {
    AddMachineTranslate,
    EditMachineTranslate,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      communityId: '',
      machineTranslateManageInfo: {
        machineTranslates: [],
        total: 0,
        records: 1,
        moreCondition: false,
        typeCds: [],
        conditions: {
          typeCd: '',
          objName: '',
          objId: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.machineTranslateManageInfo.conditions.communityId = this.communityId
    this.initData()
  },
  methods: {
    async initData() {
      try {
        await this.getDictData()
        this.listMachineTranslates()
      } catch (error) {
        console.error('初始化数据失败:', error)
      }
    },
    async getDictData() {
      try {
        const data = await getDict('machine_translate', 'type_cd')
        this.machineTranslateManageInfo.typeCds = data
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async listMachineTranslates() {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.machineTranslateManageInfo.conditions
        }
        const { machineTranslates, total } = await listMachineTranslates(params)
        this.machineTranslateManageInfo.machineTranslates = machineTranslates
        this.page.total = total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('machineTranslateManage.fetchError')
        this.$message.error(this.$t('machineTranslateManage.fetchError'))
      } finally {
        this.loading = false
      }
    },
    queryMachineTranslateMethod() {
      this.page.current = 1
      this.listMachineTranslates()
    },
    resetMachineTranslateMethod() {
      this.machineTranslateManageInfo.conditions = {
        typeCd: '',
        objName: '',
        objId: '',
        communityId: this.communityId
      }
      this.page.current = 1
      this.listMachineTranslates()
    },
    toggleMoreCondition() {
      this.machineTranslateManageInfo.moreCondition = !this.machineTranslateManageInfo.moreCondition
    },
    openEditMachineTranslateModel(row) {
      this.$refs.editMachineTranslate.open(row)
    },
    handleSuccess() {
      this.listMachineTranslates()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.listMachineTranslates()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.listMachineTranslates()
    }
  }
}
</script>

<style lang="scss" scoped>
.machine-translate-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.w-full {
  width: 100%;
}
</style>
